/**
 * CSV Export Utility
 * Handles exporting data to CSV format matching the import format
 *
 * ADR-0003 added three options — `sanitizeFormulas`, `headerLabels` and `bom`.
 * All three are OPT-IN and default to the historical behaviour, because
 * `exportImportData` / `exportTemplate` feed a round trip that the DataImport
 * parser reads back and must not change.
 */

// Leading characters Excel and Google Sheets treat as the start of a formula.
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

// UTF-8 byte order mark. Excel on Windows ignores `charset=utf-8` in a blob's
// MIME type and decodes as the system ANSI code page, which mojibakes every
// non-ASCII letter (`Łukasz Wiśniewski` -> `Å?ukasz WiÅ›niewski`,
// `Cătălin Arghiroiu` -> `CÄƒtÄƒlin Arghiroiu`). The BOM is the signal it does honour.
const UTF8_BOM = '﻿';

/**
 * Escapes CSV field values to handle commas, quotes, and newlines
 * @param {string} value - The value to escape
 * @param {Object} [options]
 * @param {boolean} [options.sanitizeFormulas=false] - Prefix a single quote to values
 *   starting with `=`, `+`, `-`, `@`, tab or CR so spreadsheets treat them as text.
 *   Opt-in: it changes the value, which would corrupt an import round trip.
 * @returns {string} - The escaped value
 */
export const escapeCSVField = (value, { sanitizeFormulas = false } = {}) => {
  if (value === null || value === undefined) {
    return '';
  }

  let stringValue = String(value);

  if (sanitizeFormulas && FORMULA_PREFIXES.includes(stringValue.charAt(0))) {
    stringValue = `'${stringValue}`;
  }

  // If the value contains comma, quote, or newline, wrap in quotes and escape internal quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Converts an array of objects to CSV format
 * @param {Array} data - Array of objects to convert
 * @param {Array} fields - Array of field names to include in the CSV
 * @param {Object} fieldConfig - Field configuration from import config
 * @param {Object} [options]
 * @param {Object} [options.headerLabels] - Map of field name to human-readable header.
 *   Fields absent from the map fall back to the field name, which is what the
 *   DataImport round trip needs.
 * @param {boolean} [options.sanitizeFormulas=false] - See escapeCSVField
 * @returns {string} - CSV formatted string
 */
export const arrayToCSV = (data, fields, fieldConfig = {}, options = {}) => {
  if (!data || data.length === 0) {
    return '';
  }

  const { headerLabels = {}, sanitizeFormulas = false } = options;

  // Header row: human-readable labels when supplied, otherwise field names (the
  // historical behaviour, which matches the import format).
  const headers = fields.map(field => escapeCSVField(headerLabels[field] ?? field));

  // Create data rows
  const rows = data.map(item => {
    return fields.map(field => {
      const config = fieldConfig[field];
      let value = item[field];

      // Handle object values (e.g., from selectors)
      if (config?.formatDisplayValue && typeof value === 'object') {
        value = config.formatDisplayValue(value);
      } else if (typeof value === 'object' && value !== null) {
        // If it's an object but no formatDisplayValue, try common properties
        if (value.email) value = value.email;
        else if (value.name) value = value.name;
        else if (value.label) value = value.label;
        else value = String(value);
      }

      return escapeCSVField(value, { sanitizeFormulas });
    });
  });

  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.join(','))
    .join('\n');

  return csvContent;
};

/**
 * Triggers a CSV download in the browser
 * @param {string} csvContent - The CSV content to download
 * @param {string} filename - The filename for the download
 * @param {Object} [options]
 * @param {boolean} [options.bom=false] - Prefix a UTF-8 BOM so Excel on Windows
 *   decodes the file as UTF-8. Required for any file containing Romanian
 *   (`ă â î ș ț`) or Polish (`ą ć ę ł ń ó ś ź ż`) names. Opt-in so the
 *   DataImport round trip is byte-for-byte unchanged.
 */
export const downloadCSV = (csvContent, filename, { bom = false } = {}) => {
  const blob = new Blob([bom ? `${UTF8_BOM}${csvContent}` : csvContent], {
    type: 'text/csv;charset=utf-8;'
  });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Exports data based on import configuration
 * @param {Array} data - Data to export
 * @param {Object} config - Import configuration object
 * @param {string} filename - Optional filename (defaults to config-based name)
 */
export const exportImportData = (data, config, filename) => {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Get all fields from config (required + optional)
  const allFields = [...config.requiredFields, ...(config.optionalFields || [])];
  
  // Export all data regardless of validation status
  const dataToExport = data;

  // Generate CSV content
  const csvContent = arrayToCSV(dataToExport, allFields, config.fields);
  
  // Generate filename if not provided
  const defaultFilename = `${config.entityNamePlural.toLowerCase().replace(/\s+/g, '_')}_export_${new Date().toISOString().split('T')[0]}.csv`;
  const exportFilename = filename || defaultFilename;
  
  // Download the file
  downloadCSV(csvContent, exportFilename);
};

/**
 * Exports template CSV with headers only (for users to fill in)
 * @param {Object} config - Import configuration object
 * @param {string} filename - Optional filename
 */
export const exportTemplate = (config, filename) => {
  // Get all fields from config (required + optional)
  const allFields = [...config.requiredFields, ...(config.optionalFields || [])];
  
  // Create empty row with just headers using field names (not labels)
  const headers = allFields.map(field => escapeCSVField(field));
  
  const csvContent = headers.join(',');
  
  // Generate filename if not provided
  const defaultFilename = `${config.entityNamePlural.toLowerCase().replace(/\s+/g, '_')}_template.csv`;
  const exportFilename = filename || defaultFilename;
  
  // Download the file
  downloadCSV(csvContent, exportFilename);
};